/**
 * User Utility Functions
 * 사용자 관련 유틸리티 함수
 * 
 * 백엔드 API DTO와 프론트엔드 타입 간 변환 함수
 */

import { UserInfoDto, OrganizationUserInfoDto } from '@/types/api/user';
import type { User } from '@/types/admin';
import {
	convertRoleNumberToString,
	organizationUserListRoleLabel,
	resolveOrganizationUserAccountKind,
} from './roleUtils';
import { formatDate } from './dateUtils';
import { apiValueToDisplayString, guestExpirationYmdForUserForm } from './apiDisplayString';

/** API가 문자열/숫자(ms)/null 혼용 시 목록용 날짜 문자열로 통일 */
function coerceUserListDateValue(v: unknown): string | undefined {
	if (v == null) return undefined;
	if (typeof v === 'number' && Number.isFinite(v)) {
		const d = new Date(v);
		return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
	}
	if (typeof v === 'string') {
		const s = v.trim();
		if (!s || s === 'null' || s === 'undefined') return undefined;
		return s;
	}
	return undefined;
}

function firstNonEmptyDateString(...candidates: (string | undefined)[]): string | undefined {
	for (const c of candidates) {
		if (typeof c === 'string' && c.trim()) return c.trim();
	}
	return undefined;
}

/**
 * GET /user/option/list 등 목록 항목에서 가입일·최종접속일 추출.
 * OpenAPI는 camelCase이나, 실서버가 snake_case·별칭으로 줄 수 있어 병행 조회.
 * 모두 없으면 undefined → 화면 '-'. (이때는 백엔드가 null을 주거나 필드 미포함인지 네트워크 탭으로 확인)
 */
export function extractUserListItemDates(item: UserInfoDto): {
	createdAt?: string;
	lastSigninAt?: string;
} {
	const raw = item as UserInfoDto & Record<string, unknown>;
	const createdAt = firstNonEmptyDateString(
		coerceUserListDateValue(item.createdAt),
		coerceUserListDateValue(raw.created_at),
		coerceUserListDateValue(raw.joinedAt),
		coerceUserListDateValue(raw.registeredAt),
		coerceUserListDateValue(raw.signupAt)
	);
	const lastSigninAt = firstNonEmptyDateString(
		coerceUserListDateValue(item.lastSigninAt),
		coerceUserListDateValue(raw.last_signin_at),
		coerceUserListDateValue(raw.lastLoginAt),
		coerceUserListDateValue(raw.last_login_at),
		coerceUserListDateValue(raw.lastAccessAt),
		coerceUserListDateValue(raw.last_sign_in_at)
	);
	return { createdAt, lastSigninAt };
}

/**
 * GET /user/option/list 등 — 스펙은 `userId`(camelCase)이나 실서버가 `user_id`·`id`만 줄 수 있음.
 * PATCH /user/admin/update-user 에 넘길 때와 목록 행 `User.id` 조립에 공통 사용.
 */
export function coalesceUserListNumericUserId(item: UserInfoDto): number | undefined {
	const raw = item as UserInfoDto & Record<string, unknown>;
	const candidates: unknown[] = [item.userId, raw.user_id, raw.id];
	for (const c of candidates) {
		if (typeof c === 'number' && Number.isFinite(c) && c >= 1) {
			return Math.floor(c);
		}
		if (typeof c === 'string') {
			const t = c.trim();
			if (/^\d+$/.test(t)) {
				const n = Number(t);
				if (Number.isFinite(n) && n >= 1) return n;
			}
		}
	}
	return undefined;
}

/** `User.id` → PATCH/삭제 API용 양의 정수 userId (빈 문자열·0·NaN 제외) */
export function parsePositiveUserId(id: User['id']): number | undefined {
	if (id == null) return undefined;
	if (typeof id === 'number') {
		return Number.isFinite(id) && id >= 1 ? Math.floor(id) : undefined;
	}
	const s = String(id).trim();
	if (!/^\d+$/.test(s)) return undefined;
	const n = Number(s);
	return Number.isFinite(n) && n >= 1 ? n : undefined;
}

/**
 * UserInfoDto를 User 타입으로 변환
 * (전체 사용자 목록 조회용)
 * 
 * @param dto - Backend API 응답 DTO
 * @returns User 타입 객체
 */
export const convertUserInfoDtoToUser = (dto: UserInfoDto, locale?: string): User => {
	const organizationName =
		apiValueToDisplayString(dto.organizationName as unknown, locale) || undefined;
	const nameFromApi = apiValueToDisplayString(dto.name as unknown, locale);
	const name = nameFromApi || dto.loginId?.trim() || '이름 없음';
	const email = apiValueToDisplayString(dto.email as unknown, locale) || undefined;
	const department = apiValueToDisplayString(dto.department as unknown, locale) || undefined;
	const position = apiValueToDisplayString(dto.position as unknown, locale) || undefined;
	const phoneNumber = apiValueToDisplayString(dto.phoneNumber as unknown, locale) || undefined;

	const uid = coalesceUserListNumericUserId(dto);

	return {
		id: uid != null ? uid : dto.userId,
		name,
		email,
		type: dto.type === 'REGULAR' ? 'regular' : 'guest',
		accountType: dto.type === 'REGULAR' ? '정회원' : '게스트',
		customerType: organizationName ? '기관고객' : '개인고객',
		organizationName,
		role: convertRoleNumberToString(dto.role),
		registeredDate: formatDate(dto.createdAt, locale),
		lastLogin: dto.lastSigninAt ? formatDate(dto.lastSigninAt, locale) : undefined,
		status: dto.status ? 'active' : 'inactive',
		department,
		position,
		phoneNumber,
		loginId: dto.loginId,
	};
};

/**
 * OrganizationUserInfoDto를 User 타입으로 변환
 * (기관별 사용자 목록 조회용)
 * 
 * @param dto - Backend API 응답 DTO
 * @returns User 타입 객체
 */
export const convertOrganizationUserInfoDtoToUser = (
	dto: OrganizationUserInfoDto,
	locale?: string
): User => {
	const nameFromApi = apiValueToDisplayString(dto.name as unknown, locale);
	const name = nameFromApi || dto.loginId?.trim() || '이름 없음';
	const email = apiValueToDisplayString(dto.email as unknown, locale) || undefined;
	const accountKind = resolveOrganizationUserAccountKind(dto);
	const expirationDate = guestExpirationYmdForUserForm({
		isGuest: accountKind === 'ASSOCIATE',
		guestAccountExpiresAt: dto.guestAccountExpiresAt,
		createdAt: dto.createdAt,
		raw: dto as unknown as Record<string, unknown>,
		locale,
	});

	return {
		id: dto.userId.toString(),
		name,
		email,
		type: accountKind === 'REGULAR' ? 'regular' : 'guest',
		accountType: accountKind === 'REGULAR' ? '정회원' : '게스트',
		customerType: '기관고객',
		organizationName: undefined, // 기관별 조회이므로 이미 기관 컨텍스트 내
		role: organizationUserListRoleLabel(dto),
		...(expirationDate ? { expirationDate } : {}),
		registeredDate: formatDate(dto.createdAt, locale),
		lastLogin: dto.lastSigninAt ? formatDate(dto.lastSigninAt, locale) : undefined,
		status: 'active', // OrganizationUserInfoDto에는 status 필드 없음
		// 추가 필드
		department: dto.department,
		loginId: dto.loginId,
	};
};

/**
 * 사용자 계정 타입 변환
 * 
 * @param accountType - 백엔드 계정 타입
 * @returns 프론트엔드 계정 타입
 */
export const convertAccountType = (
	accountType: 'REGULAR' | 'ASSOCIATE'
): 'regular' | 'guest' => {
	return accountType === 'REGULAR' ? 'regular' : 'guest';
};

/**
 * 사용자 계정 타입 표시명 변환
 * 
 * @param accountType - 백엔드 계정 타입
 * @returns 표시명
 */
export const convertAccountTypeToDisplay = (
	accountType: 'REGULAR' | 'ASSOCIATE'
): string => {
	const displayMap: Record<'REGULAR' | 'ASSOCIATE', string> = {
		REGULAR: '정회원',
		ASSOCIATE: '게스트',
	};
	return displayMap[accountType] || accountType;
};
